import { Box, Button, Chip, Stack, Tooltip } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import PaymentsIcon from '@mui/icons-material/Payments'
import PlaceIcon from '@mui/icons-material/Place'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import BusinessIcon from '@mui/icons-material/Business'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import { makeGetRequest, makePostRequest } from 'utils/api'
import moment from 'moment'
import { toast } from 'react-toastify'
import CreateTask from './CreateTask'
import CreateEvent from './CreateEvent'

// ── Design tokens (cohérents avec le thème sombre LIFE) ───────────────────────
const C = {
  panel: '#0c1e3a',
  card: '#102a4d',
  cardHover: '#143258',
  cardActive: '#16386b',
  border: '1px solid rgba(193,218,241,0.12)',
  borderActive: '1px solid #1b75d0',
  accent: 'linear-gradient(135deg,#1b75d0 0%,#196081 100%)',
  textHi: '#eaf2fb',
  textLo: '#93a7c4',
  chipBg: 'rgba(27,117,208,0.16)',
}

export default function Offer() {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [joblist, setJobList] = useState([])
  const [filterJoblist, setFilterJobList] = useState([])
  const [selectedJob, setSelectedJob] = useState(false)
  const [createTache, setCreateTache] = useState(false)
  const [createEvent, setCreateEvent] = useState(false)
  const [savedIds, setSavedIds] = useState([])

  useEffect(() => {
    getJobList()
  }, [])

  const handleJob = async (job, status) => {
    const payload = { id: job.id, status: status }
    const res = await makePostRequest('/api/Professional/jobs-status/', payload, {}, true)
    if (res.status === 200) {
      if (status === 'Enregistrer') setSavedIds((prev) => [...new Set([...prev, job.id])])
      toast(`${job.title} — ${status === 'Postuler' ? t('opportunities.btnApply') : t('opportunities.btnSave')} ✓`, {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        theme: 'dark',
        pauseOnFocusLoss: false,
      })
    }
  }
  const getJobList = async () => {
    const res = await makeGetRequest('/api/Professional/jobs/', {}, true)
    if (res.status === 200) {
      setJobList(res.data)
      setFilterJobList(res.data)
    }
  }
  const handleInputChange = (event) => {
    let query = event.target.value
    if (query !== '') {
      setFilterJobList(
        joblist.filter(
          (el) =>
            (el.title || '').toLowerCase().includes(query.toLowerCase()) ||
            (el.company_name || '').toLowerCase().includes(query.toLowerCase()) ||
            (el.location || '').toLowerCase().includes(query.toLowerCase()),
        ),
      )
    } else {
      setFilterJobList(joblist)
    }
  }
  const toggleModal = () => setCreateEvent(false)

  return (
    <>
      {createEvent && <CreateEvent toggleModal={toggleModal} title={selectedJob.title} />}
      {createTache && <CreateTask setCreateTache={setCreateTache} title={selectedJob.title} />}

      <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        {/* ── Colonne liste ─────────────────────────────────────────────── */}
        <Box
          sx={{
            width: { xs: '100%', md: 400 },
            minWidth: { md: 400 },
            height: '82vh',
            display: 'flex',
            flexDirection: 'column',
            background: C.panel,
            border: C.border,
            borderRadius: 3,
            p: 2,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.textHi }}>
              {t('opportunities.titleJobOffers') /* LAP-733 (GE022) */}
            </Typography>
            <Chip
              label={filterJoblist.length}
              size="small"
              sx={{ background: C.chipBg, color: '#74b7fd', fontWeight: 700 }}
            />
          </Stack>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: '#0a1730',
              border: C.border,
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              mb: 2,
            }}
          >
            <SearchIcon sx={{ color: C.textLo, fontSize: 20 }} />
            <input
              onChange={handleInputChange}
              placeholder={t('opportunities.placeholderLocation') /* LAP-733 (GE022) */}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: C.textHi,
                fontSize: 14,
                padding: '8px 0',
              }}
            />
          </Box>

          <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
            {filterJoblist.length > 0 ? (
              <Stack spacing={1.25}>
                {filterJoblist.map((job, index) => {
                  const active = selectedJob && selectedJob.id === job.id
                  const saved = savedIds.includes(job.id)
                  return (
                    <Box
                      key={index}
                      onClick={() => setSelectedJob(job)}
                      sx={{
                        cursor: 'pointer',
                        background: active ? C.cardActive : C.card,
                        border: active ? C.borderActive : C.border,
                        borderRadius: 2.5,
                        p: 1.5,
                        transition: 'all .15s ease',
                        '&:hover': { background: C.cardHover, transform: 'translateY(-1px)' },
                      }}
                    >
                      <Stack direction="row" spacing={1.5}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: C.accent,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <BusinessIcon sx={{ color: 'white' }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 15,
                              fontWeight: 700,
                              color: C.textHi,
                              lineHeight: 1.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {job.title}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: '#c1daf1' }}>
                            {job.company_name}
                          </Typography>
                          {job.location && (
                            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
                              <PlaceIcon sx={{ fontSize: 13, color: C.textLo }} />
                              <Typography sx={{ fontSize: 12, color: C.textLo }}>
                                {job.location}
                              </Typography>
                            </Stack>
                          )}
                          <Typography sx={{ fontSize: 11, color: C.textLo, mt: 0.5 }}>
                            {moment(job.created_at).fromNow()}
                          </Typography>
                        </Box>
                        <Tooltip title={t('opportunities.btnSave')}>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation()
                              handleJob(job, 'Enregistrer')
                            }}
                            sx={{ color: saved ? '#e5aa20' : C.textLo, '&:hover': { color: '#e5aa20' } }}
                          >
                            {saved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                          </Box>
                        </Tooltip>
                      </Stack>
                    </Box>
                  )
                })}
              </Stack>
            ) : (
              <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: '100%', color: C.textLo }}>
                <TravelExploreIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                <Typography sx={{ textAlign: 'center', fontSize: 14 }}>
                  {t('opportunities.noJobsFound') /* LAP-733 (GE022) */}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>

        {/* ── Colonne détail ────────────────────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            height: '82vh',
            overflowY: 'auto',
            background: C.panel,
            border: C.border,
            borderRadius: 3,
            p: { xs: 2, md: 3 },
          }}
        >
          {selectedJob ? (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 3,
                    background: C.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BusinessIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, color: '#c1daf1', fontWeight: 600 }}>
                    {selectedJob.company_name}
                  </Typography>
                  <Typography sx={{ fontSize: 26, fontWeight: 800, color: C.textHi, lineHeight: 1.2 }}>
                    {selectedJob.title}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
                {selectedJob.location && (
                  <Chip
                    icon={<PlaceIcon sx={{ color: '#74b7fd !important' }} />}
                    label={selectedJob.location}
                    sx={{ background: C.chipBg, color: C.textHi }}
                  />
                )}
                {selectedJob.salary_range && (
                  <Chip
                    icon={<PaymentsIcon sx={{ color: '#74b7fd !important' }} />}
                    label={selectedJob.salary_range}
                    sx={{ background: C.chipBg, color: C.textHi }}
                  />
                )}
                {selectedJob.contract_type && (
                  <Chip
                    icon={<WorkOutlineIcon sx={{ color: '#74b7fd !important' }} />}
                    label={selectedJob.contract_type}
                    sx={{ background: C.chipBg, color: C.textHi }}
                  />
                )}
              </Stack>

              <Stack direction="row" flexWrap="wrap" gap={1.5} mt={3}>
                <Button
                  size="large"
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => handleJob(selectedJob, 'Postuler')}
                  sx={{
                    background: C.accent,
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 6px 18px rgba(27,117,208,0.35)',
                    '&:hover': { background: 'linear-gradient(135deg,#2a86e0 0%,#1f6f93 100%)' },
                  }}
                >
                  {t('opportunities.btnApply') /* LAP-733 (GE022) */}
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<BookmarkBorderIcon />}
                  onClick={() => handleJob(selectedJob, 'Enregistrer')}
                  sx={{
                    color: C.textHi,
                    borderColor: 'rgba(193,218,241,0.35)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#74b7fd', background: 'rgba(27,117,208,0.08)' },
                  }}
                >
                  {t('opportunities.btnSave') /* LAP-733 (GE022) */}
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<PlaylistAddCheckIcon />}
                  onClick={() => setCreateTache(true)}
                  sx={{
                    color: C.textHi,
                    borderColor: 'rgba(193,218,241,0.35)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#74b7fd', background: 'rgba(27,117,208,0.08)' },
                  }}
                >
                  {t('opportunities.btnToDo') /* LAP-733 (GE022) */}
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => setCreateEvent(true)}
                  sx={{
                    color: C.textHi,
                    borderColor: 'rgba(193,218,241,0.35)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#74b7fd', background: 'rgba(27,117,208,0.08)' },
                  }}
                >
                  {t('opportunities.btnEvent') /* LAP-733 (GE022) */}
                </Button>
                {selectedJob.url && (
                  <Button
                    size="large"
                    variant="text"
                    endIcon={<OpenInNewIcon />}
                    href={selectedJob.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#74b7fd', textTransform: 'none', fontWeight: 600 }}
                  >
                    {selectedJob.source ? selectedJob.source : 'Source'}
                  </Button>
                )}
              </Stack>

              <Box
                sx={{
                  mt: 3,
                  p: 2.5,
                  background: C.card,
                  border: C.border,
                  borderRadius: 2.5,
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.textHi, mb: 1 }}>
                  {t('opportunities.aboutOffer') /* LAP-733 (GE022) */}
                </Typography>
                <Typography sx={{ fontSize: 14, color: '#c1daf1', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                  {selectedJob.description || '—'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: '100%', color: C.textLo }}>
              <WorkOutlineIcon sx={{ fontSize: 64, opacity: 0.4 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                {t('opportunities.titleJobOffers')}
              </Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.8 }}>
                Sélectionne une offre à gauche pour voir le détail.
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </>
  )
}
